import { Location } from "@angular/common";
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
	TemplateRef,
	ViewChild,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { isEmpty } from "lodash";
import moment from "moment";
import { Columns, Config, DefaultConfig, Pagination } from "ngx-easy-table";
import { Subscription } from "rxjs";
import { CONSTANT } from "src/app/constant";
import { RouteType } from "src/app/models/enum";
import { PaginationDetails } from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import {
	ErrorSnackbarComponent,
	ErrorSnackbarData,
} from "src/app/shared/components/cbta-snackbar/error-snackbar.component";
import { DateRange } from "src/app/shared/constants/angular-datetime-picker.const";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { getFormTypeShortLabel } from "src/app/shared/utils/form-type-label";
import { parseAocIdsFromQuery } from "src/app/util/aoc-filter-options.utils";
import { formatAttachedFormNames, formatUtcIsoForLocalDisplay } from "../feedback-template.mapper";
import { FeedbackListParams, FeedbackTemplateListItem } from "../feedback.types";

@Component({
	selector: "app-feedback-management",
	templateUrl: "./feedback-management.component.html",
	styleUrls: ["./feedback-management.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class FeedbackManagementComponent implements OnInit, OnDestroy, AfterViewInit {
	@ViewChild("titleTpl", { static: true }) titleTpl!: TemplateRef<any>;
	@ViewChild("formNameTpl", { static: true }) formNameTpl!: TemplateRef<any>;
	@ViewChild("formTypeTpl", { static: true }) formTypeTpl!: TemplateRef<any>;
	@ViewChild("dateTpl", { static: true }) dateTpl!: TemplateRef<any>;
	@ViewChild("actionTpl", { static: true }) actionTpl!: TemplateRef<any>;
	@ViewChild("aocTpl", { static: true }) aocTpl!: TemplateRef<any>;

	columns: Columns[] = [];
	configuration: Config = { ...DefaultConfig };
	templates: FeedbackTemplateListItem[] = [];
	isOnline = true;

	filters?: FeedbackListParams = {
		page: this.constant.PAGINATION.DEFAULT_PAGE,
		size: this.constant.PAGINATION.DEFAULT_SIZE,
	};

	pagination: Pagination = {
		limit: this.constant.PAGINATION.DEFAULT_SIZE,
		offset: 1,
		count: 1,
	};

	searchQuery = "";
	selectedFormType = "";
	selectedAocIds: string[] = [];
	formTypeFilterOptions: { label: string; value: string }[] = [];
	dateFilterEnabled = false;
	startDate: Date = new Date(new Date().getFullYear(), new Date().getMonth() - 3, 1);
	endDate: Date = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0);

	private templatesSub?: Subscription;
	private routeSub?: Subscription;
	private searchTimeout?: ReturnType<typeof setTimeout>;

	constructor(
		private sharedService: SharedService,
		private httpService: HttpService,
		private loaderService: LoaderService,
		private router: Router,
		private route: ActivatedRoute,
		private location: Location,
		private roleService: RoleService,
		private cdr: ChangeDetectorRef,
		private dialog: MatDialog,
		private snackBar: MatSnackBar,
	) {
		this.formTypeFilterOptions = [
			...this.sharedService.filterOptions.map((option) => ({
				value: option.value,
				label: getFormTypeShortLabel(option.value),
			})),
			{ label: "Standalone", value: "standalone" },
		];
		const params = this.route.snapshot.queryParams;
		if (params["startDate"] && params["endDate"]) {
			this.dateFilterEnabled = true;
			this.startDate = moment(params["startDate"]).toDate();
			this.endDate = moment(params["endDate"]).toDate();
		}
	}

	ngOnInit(): void {
		this.sharedService.setActivePage(RouteType.FEEDBACK);
		this.sharedService.getIsStatusOnline().subscribe((status) => {
			this.isOnline = status;
			this.cdr.detectChanges();
		});
		this.initTableConfig();
	}

	ngAfterViewInit(): void {
		this.initParams();
	}

	ngOnDestroy(): void {
		this.templatesSub?.unsubscribe();
		this.routeSub?.unsubscribe();
		if (this.searchTimeout) clearTimeout(this.searchTimeout);
	}

	get constant() {
		return CONSTANT;
	}

	get page(): number {
		return parseInt(`${this.filters?.page ?? this.constant.PAGINATION.DEFAULT_PAGE}`);
	}

	get size(): number {
		return parseInt(`${this.filters?.size ?? this.constant.PAGINATION.DEFAULT_SIZE}`);
	}

	initTableConfig(): void {
		this.configuration = { ...DefaultConfig };
		this.configuration.searchEnabled = false;
		this.configuration.tableLayout.style = "bigger";
		this.configuration.paginationMaxSize = this.constant.PAGINATION.MAX_PAGINATION_COUNT;
		this.columns = [
			...(this.isGroupAdmin ? [{ key: "aocCode", title: "AOC", cellTemplate: this.aocTpl, width: "10%" }] : []),
			{
				key: "formTypeLabel",
				title: "Attachment",
				cellTemplate: this.formTypeTpl,
				width: this.isGroupAdmin ? "18%" : "22%",
			},
			{
				key: "formName",
				title: "Attached Test Name",
				cellTemplate: this.formNameTpl,
				width: this.isGroupAdmin ? "20%" : "24%",
			},
			{
				key: "name",
				title: "Feedback Form Title",
				cellTemplate: this.titleTpl,
				width: this.isGroupAdmin ? "20%" : "24%",
			},
			{
				key: "lastUpdated",
				title: "Last Updated",
				cellTemplate: this.dateTpl,
				width: "16%",
			},
			{ key: "", title: "Action", cellTemplate: this.actionTpl, width: "14%" },
		];
	}

	get isGroupAdmin(): boolean {
		return this.roleService.isGroupAdmin;
	}

	get aocFilterOptions() {
		return this.sharedService.aocFilterOptions;
	}

	initParams(): void {
		this.routeSub = this.route.queryParams.subscribe((params) => {
			this.searchQuery = params["search"] ?? "";
			this.selectedAocIds = params["aocIds"]?.length ? params["aocIds"] : parseAocIdsFromQuery(params["aocId"]);
			this.selectedFormType = this.isGroupAdmin ? "" : (params["formType"] ?? "");
			if (params["startDate"] && params["endDate"]) {
				this.dateFilterEnabled = true;
				this.startDate = moment(params["startDate"]).toDate();
				this.endDate = moment(params["endDate"]).toDate();
			} else {
				this.dateFilterEnabled = false;
			}
			this.updateFilters(
				this.buildListFilters({
					page: params["page"] ? parseInt(params["page"], 10) : this.constant.PAGINATION.DEFAULT_PAGE,
					size: params["size"] ? parseInt(params["size"], 10) : this.constant.PAGINATION.DEFAULT_SIZE,
				}),
			);
		});
	}

	initTemplateList(): void {
		this.loaderService.setLoading(true);
		this.templatesSub?.unsubscribe();
		this.templatesSub = this.httpService.listFeedbackTemplates(this.filters).subscribe({
			next: (result) => {
				const payload = result?.data ?? result;
				this.templates = (payload?.data ?? []) as FeedbackTemplateListItem[];
				const total: number = payload?.total ?? 0;
				const paginationDetails = this.calculatePages(total, this.size);
				this.pagination = {
					limit: this.size,
					offset: this.page,
					count: this.size * paginationDetails.totalPages,
				};
				this.cdr.detectChanges();
				this.loaderService.setLoading(false);
			},
			error: () => this.loaderService.setLoading(false),
		});
	}

	onTableChanged(event: any): void {
		if (event.event !== "onPagination") return;
		const value = event.value;
		this.updateFilters(
			this.buildListFilters({
				page: value.page,
				size: value.limit,
			}),
		);
	}

	onSearch(): void {
		if (this.searchTimeout) clearTimeout(this.searchTimeout);
		this.searchTimeout = setTimeout(() => {
			this.updateFilters(this.buildListFilters({ page: 1 }));
		}, 300);
	}

	onFormTypeFilter(formType: string): void {
		this.selectedFormType = formType ?? "";
		this.updateFilters(this.buildListFilters({ page: 1, formType: this.selectedFormType }));
	}

	onAocFilterChanged(aocIds: string[]): void {
		this.selectedAocIds = aocIds ?? [];
		this.updateFilters(
			this.buildListFilters({
				page: 1,
				aocIds: this.selectedAocIds.length ? this.selectedAocIds : undefined,
			}),
		);
	}

	handleDateRangeChange(dateRange: DateRange): void {
		if (dateRange?.enabled === false) {
			this.dateFilterEnabled = false;
		} else if (dateRange?.startDate && dateRange?.endDate) {
			this.dateFilterEnabled = true;
			this.startDate = dateRange.startDate;
			this.endDate = dateRange.endDate;
		} else {
			return;
		}

		this.updateFilters(this.buildListFilters({ page: 1 }));
	}

	private buildListFilters(overrides: Partial<FeedbackListParams> = {}): FeedbackListParams {
		const filters: FeedbackListParams = {
			page: overrides.page ?? this.page,
			size: overrides.size ?? this.size,
			role: this.roleService.getUserRole(),
		};

		const search = (overrides.search ?? this.searchQuery).trim();
		if (search) {
			filters.search = search;
		}

		const selectedAocIds = overrides.aocIds?.length ? overrides.aocIds : this.selectedAocIds;
		const formType = (overrides.formType ?? this.selectedFormType).trim();
		if (!this.isGroupAdmin && formType) {
			filters.formType = formType;
		}

		if (this.isGroupAdmin && selectedAocIds.length) {
			filters.aocIds = selectedAocIds;
		}

		if (this.dateFilterEnabled) {
			filters.startDate = moment(this.startDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT);
			filters.endDate = moment(this.endDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT);
		}

		return filters;
	}

	updateFilters(filters: FeedbackListParams): void {
		this.selectedFormType = filters.formType?.trim() ?? "";
		this.filters = {
			page: filters.page ?? this.constant.PAGINATION.DEFAULT_PAGE,
			size: filters.size ?? this.constant.PAGINATION.DEFAULT_SIZE,
			role: filters.role ?? this.roleService.getUserRole(),
			...(filters.search?.trim() ? { search: filters.search.trim() } : {}),
			...(filters.formType?.trim() ? { formType: filters.formType.trim() } : {}),
			...(filters.aocIds?.length ? { aocIds: filters.aocIds } : {}),
			...(filters.startDate ? { startDate: filters.startDate } : {}),
			...(filters.endDate ? { endDate: filters.endDate } : {}),
		};

		if (!isEmpty(filters.size)) {
			this.configuration.rows = this.size;
		}

		this.updateUrl();
		this.initTemplateList();
	}

	updateUrl(): void {
		const queryParams: FeedbackListParams = {
			page: this.page,
			size: this.size,
			role: this.roleService.getUserRole(),
		};
		if (this.searchQuery.trim()) {
			queryParams.search = this.searchQuery.trim();
		}
		if (this.selectedFormType) {
			queryParams.formType = this.selectedFormType;
		}
		if (this.isGroupAdmin && this.selectedAocIds.length) {
			queryParams.aocIds = this.selectedAocIds;
		}
		if (this.filters?.startDate) {
			queryParams.startDate = this.filters.startDate;
		}
		if (this.filters?.endDate) {
			queryParams.endDate = this.filters.endDate;
		}

		const url = this.router.serializeUrl(this.router.createUrlTree([RouteType.FEEDBACK], { queryParams }));
		this.location.go(url);
	}

	createNew(): void {
		this.router.navigate([RouteType.FEEDBACK, "new"]);
	}

	editTemplate(id: string): void {
		this.router.navigate([RouteType.FEEDBACK, id, "edit"]);
	}

	viewResponses(id: string): void {
		const queryParams: { aocIds?: string[] } = {};
		if (this.isGroupAdmin && this.selectedAocIds.length) {
			queryParams.aocIds = this.selectedAocIds;
		}
		this.router.navigate([RouteType.FEEDBACK, id, "responses"], {
			queryParams,
		});
	}

	formatDate(value?: string): string {
		if (!value) return "-";
		return moment(value).format(CONSTANT.DATE_FORMAT.TABLE_DATE_FORMAT);
	}

	hasScheduleWindow(row: FeedbackTemplateListItem): boolean {
		return row.isStandalone === true && !!(row.startsAt || row.endsAt);
	}

	formatScheduleWindow(row: FeedbackTemplateListItem): string {
		if (!this.hasScheduleWindow(row)) return "";
		const start = row.startsAt ? formatUtcIsoForLocalDisplay(row.startsAt) : "—";
		const end = row.endsAt ? formatUtcIsoForLocalDisplay(row.endsAt) : "—";
		return `${start} → ${end}`;
	}

	isScheduled(row: FeedbackTemplateListItem): boolean {
		if (!row.startsAt || row.dispatchedAt) return false;
		return new Date(row.startsAt).getTime() > Date.now();
	}

	canSendForm(row: FeedbackTemplateListItem): boolean {
		if (!row.isStandalone || !row.isActive || !this.isOnline) return false;
		const now = Date.now();
		if (row.endsAt && new Date(row.endsAt).getTime() <= now) return false;
		return true;
	}

	scheduledStartLabel(row: FeedbackTemplateListItem): string {
		if (!row.startsAt) return "";
		return formatUtcIsoForLocalDisplay(row.startsAt);
	}

	private buildDispatchMessage(payload: {
		tasksCreated?: number;
		tasksSkipped?: number;
		emailsQueued?: number;
		emailsSent?: number;
		emailsPending?: number;
	}): string {
		const created = payload.tasksCreated ?? 0;
		const skipped = payload.tasksSkipped ?? 0;
		const emailsQueued = payload.emailsQueued ?? 0;
		const emailsSent = payload.emailsSent ?? 0;
		const emailsPending = payload.emailsPending ?? 0;
		const totalUsers = created + skipped;

		if (totalUsers === 0) {
			return "No users in this AOC match the selected recipient roles.";
		}

		if (emailsQueued > 0 && emailsSent === 0) {
			return `Feedback queued for ${totalUsers} user(s). ${emailsQueued} email(s) are being sent.`;
		}
		if (emailsSent > 0 && emailsPending > 0) {
			return `Feedback sent to ${emailsSent} user(s). ${emailsPending} email(s) are still sending.`;
		}
		if (emailsSent > 0) {
			return `Feedback sent to ${emailsSent} user(s)` + (skipped ? ` (${skipped} already pending)` : "");
		}
		if (totalUsers > 0 && emailsPending > 0) {
			return `Feedback is in the checklist for ${totalUsers} user(s). ${emailsPending} email(s) are still sending.`;
		}
		return `Feedback sent to ${created} user(s)` + (skipped ? ` (${skipped} already pending)` : "");
	}

	getFormNames(row: FeedbackTemplateListItem): string {
		return formatAttachedFormNames(row);
	}

	getFormTypeLabel(row: FeedbackTemplateListItem): string {
		if (row.isStandalone) {
			return "Standalone";
		}
		return row.formTypeLabel || "-";
	}

	canDelete(row: FeedbackTemplateListItem): boolean {
		return !this.isGroupAdmin && !row.hasResponses && this.isOnline;
	}

	deleteTemplate(row: FeedbackTemplateListItem): void {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "feedback-delete-modal",
			width: "520px",
			hasBackdrop: true,
			data: {
				modalContent: `Are you sure you want to delete the feedback form '${row.name}'?`,
				modalBtnConfirmText: "Delete",
				modalBtnCancelText: "Cancel",
				noSaveBtn: false,
			},
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (!result?.close) return;

			this.loaderService.setLoading(true);
			this.httpService.deleteFeedbackTemplate(row.id).subscribe({
				next: () => {
					this.loaderService.setLoading(false);
					this.snackBar.open("Feedback form deleted successfully.", undefined, {
						duration: 5000,
						horizontalPosition: "center",
						verticalPosition: "top",
					});

					if (this.templates.length === 1 && this.page > 1) {
						this.updateFilters({
							...this.filters,
							page: this.page - 1,
						});
					} else {
						this.initTemplateList();
					}
				},
				error: (err) => {
					this.loaderService.setLoading(false);
					const message = err?.error?.message ?? err?.error?.data?.message ?? "Unable to delete feedback form";
					this.snackBar.openFromComponent(ErrorSnackbarComponent, {
						data: { message } as ErrorSnackbarData,
						duration: 5000,
						horizontalPosition: "center",
						verticalPosition: "top",
						panelClass: ["error-snackbar"],
					});
				},
			});
		});
	}

	sendOut(row: FeedbackTemplateListItem): void {
		const recipientLabel = row.recipientRoleLabels || "selected recipients";
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "feedback-dispatch-modal",
			width: "520px",
			hasBackdrop: true,
			data: {
				modalContent: `Are you sure you want to send out the feedback form to '${recipientLabel}'?`,
				modalBtnConfirmText: "Continue",
				modalBtnCancelText: "Cancel",
				noSaveBtn: false,
			},
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (!result?.close) return;

			this.loaderService.setLoading(true);
			this.httpService.dispatchFeedbackTemplate(row.id).subscribe({
				next: (resp) => {
					const payload = resp?.data ?? resp;
					this.loaderService.setLoading(false);
					this.snackBar.open(this.buildDispatchMessage(payload), undefined, {
						duration: 5000,
						horizontalPosition: "center",
						verticalPosition: "top",
					});
					this.initTemplateList();
				},
				error: (err) => {
					this.loaderService.setLoading(false);
					const message = err?.error?.message ?? err?.error?.data?.message ?? "Unable to send feedback form";
					this.snackBar.openFromComponent(ErrorSnackbarComponent, {
						data: { message } as ErrorSnackbarData,
						duration: 5000,
						horizontalPosition: "center",
						verticalPosition: "top",
						panelClass: ["error-snackbar"],
					});
				},
			});
		});
	}

	calculatePages(totalDataCount: number, rowsPerPage: number): PaginationDetails {
		const totalPages = Math.ceil(totalDataCount / rowsPerPage) || 1;
		const lastPageSize = totalDataCount % rowsPerPage;
		return { totalPages, lastPageSize };
	}
}
